import React from "react";
import PropTypes from "prop-types";
import { ActionsTypes } from "../../constants";
import { AccessRightsModal } from "../access-rights-modal";
import { useUpdateAttendeesAccessRights } from "pages/attendees-list/components/batchs-actions/hooks/use-update-attendees-access-rights";
import { useAttendeesBatchActionsToasts } from "pages/attendees-list/components/batchs-actions/hooks/useAttendeesBatchActionsToasts";
import useOrganizationContext from "hooks/useOrganizationContext";

export const BatchActionsModals = ({
	openedType,
	onSuccess,
	onCancel,
	attendeeIds,
	eventId
}) => {
	const organization = useOrganizationContext();
	const { success, error } = useAttendeesBatchActionsToasts();

	const organizationId = organization?.id();
	const [submittedType, setSubmittedType] = React.useState(null);

	const buildPayload = React.useCallback(
		(selectedCheckpoints, currentOpenedType) => {
			const isAddRights = currentOpenedType === ActionsTypes.ADD_RIGHTS;
			const action = isAddRights
				? "add_accessrights"
				: "remove_accessrights";

			const checkpoints = selectedCheckpoints.map(checkpoint => {
				const checkpointPayload = {
					checkpoint_id: checkpoint.id
				};

				if (isAddRights) {
					const isUsingStrictMode = checkpoint.is_using_strict_mode;

					if (isUsingStrictMode) {
						// If using strict mode: strict_count takes strict_nb_access and count is set to null
						checkpointPayload.strict_count =
							checkpoint.strict_nb_access === null
								? 999
								: checkpoint.strict_nb_access;
						checkpointPayload.count = null;
					} else {
						// If not using strict mode: count takes default_nb_access and strict_count is null
						checkpointPayload.count = checkpoint.default_nb_access;
						checkpointPayload.strict_count = null;
					}
				}

				return checkpointPayload;
			});

			return {
				type: "update_attendees_access_rights",
				config: {
					action,
					attendee_ids: attendeeIds,
					checkpoints
				}
			};
		},
		[attendeeIds]
	);

	const { mutate, isLoading } = useUpdateAttendeesAccessRights(
		organizationId,
		{
			showErrorToast: false,
			onSuccess: () => {
				success(submittedType);
				setSubmittedType(null);
				onSuccess();
			},
			onError: () => {
				error(submittedType);
				setSubmittedType(null);
				onCancel();
			}
		}
	);

	const handleSubmit = selectedCheckpoints => {
		setSubmittedType(openedType);
		const payload = buildPayload(selectedCheckpoints, openedType);
		onCancel();
		mutate(payload);
	};

	return (
		<>
			{openedType === ActionsTypes.ADD_RIGHTS && (
				<AccessRightsModal
					actionType={ActionsTypes.ADD_RIGHTS}
					attendeeIds={attendeeIds}
					isSubmitting={isLoading}
					onCancel={onCancel}
					onSubmit={handleSubmit}
					eventId={eventId}
				/>
			)}
			{openedType === ActionsTypes.REMOVE_RIGHTS && (
				<AccessRightsModal
					actionType={ActionsTypes.REMOVE_RIGHTS}
					attendeeIds={attendeeIds}
					isSubmitting={isLoading}
					onCancel={onCancel}
					onSubmit={handleSubmit}
					eventId={eventId}
				/>
			)}
		</>
	);
};

BatchActionsModals.propTypes = {
	openedType: PropTypes.string,
	onSuccess: PropTypes.func,
	onCancel: PropTypes.func,
	attendeeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
	eventId: PropTypes.number
};

BatchActionsModals.defaultProps = {
	openedType: null,
	onSuccess: () => {},
	onCancel: () => {},
	eventId: null
};
